import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchChannels, fetchLibrary, fetchBooks, fetchContinueWatching } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import VideoPlayer from "@/components/VideoPlayer";
import ChannelGuide from "@/components/ChannelGuide";
import CategoryRow from "@/components/CategoryRow";
import ContentCard from "@/components/ContentCard";
import ChannelTilePreview from "@/components/ChannelTilePreview";
import AdSlot from "@/components/AdSlot";
import FeaturedAd from "@/components/FeaturedAd";
import { Radio, Sparkles, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

export default function LiveTV() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [channels, setChannels] = useState([]);
  const [library, setLibrary] = useState([]);
  const [books, setBooks] = useState([]);
  const [cw, setCw] = useState([]);
  const [activeSlug, setActiveSlug] = useState(slug || "vot-family");

  useEffect(() => {
    fetchChannels().then(setChannels);
    fetchLibrary().then(setLibrary);
    fetchBooks().then(setBooks);
  }, []);
  useEffect(() => {
    if (user) fetchContinueWatching().then(setCw).catch(()=>{});
    else setCw([]);
  }, [user]);
  useEffect(() => { if (slug) setActiveSlug(slug); }, [slug]);

  const active = useMemo(() => channels.find(c => c.slug === activeSlug) || channels[0], [channels, activeSlug]);
  const handleSelect = (s) => { setActiveSlug(s); nav(`/live/${s}`); };
  const byCategory = (cat) => library.filter(i => i.category === cat);
  const categories = ["Trending Faith", "Sunday Sermons", "Worship Concerts", "Documentaries", "Kids & Family", "Movies"];

  return (
    <div className="pt-16" data-testid="live-tv-page">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-[#D4AF37]">Live Broadcast</span>
          </div>
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            {active ? (
              <VideoPlayer
                key={active.slug}
                src={active.stream_url}
                streamType={active.stream_type || "hls"}
                poster={active.logo_url}
                title={active.now_playing?.title || active.name}
                subtitle={`${active.name} • ${active.now_playing?.host || "Live Broadcast"}`}
              />
            ) : <div className="aspect-video rounded-2xl bg-[#121216] animate-pulse" />}

            <aside className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-5" data-testid="channel-selector">
              <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-white/50 mb-3">Now Streaming</p>
              <div className="space-y-3">
                {channels.map((ch) => (
                  <ChannelTileButton
                    key={ch.id}
                    channel={ch}
                    isActive={ch.slug === activeSlug}
                    onSelect={() => handleSelect(ch.slug)}
                  />
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-[#D4AF37]" /><p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-white/60">Coming Up</p></div>
                {active?.schedule?.slice(2, 5).map((p) => (
                  <div key={p.id} className="py-2 border-b border-white/5 last:border-b-0" data-testid={`upcoming-${p.id}`}>
                    <p className="text-xs text-white/40">{new Date(p.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
                    <p className="text-sm text-white/90 font-medium truncate font-heading">{p.title}</p>
                  </div>
                ))}
              </div>

              {/* Cush Community Development affiliate promo */}
              <a
                href="https://www.facebook.com/profile.php?id=100089116817821"
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid="cush-affiliate-card"
                className="mt-5 block rounded-xl overflow-hidden border border-[#D4AF37]/40 bg-gradient-to-br from-[#1a1108] via-[#0d0a05] to-[#0A0A0E] hover:border-[#D4AF37] transition-all group"
              >
                <div className="relative h-24 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=600&h=200&fit=crop"
                    alt="Community outreach"
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/40 to-transparent" />
                  <span className="absolute top-2 left-2 text-[9px] font-bold tracking-[0.2em] uppercase bg-[#D4AF37] text-[#050507] px-2 py-0.5 rounded">Affiliate</span>
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#D4AF37]">Ministry Partner</p>
                  <h4 className="font-heading text-base font-bold text-white mt-1 leading-tight">Cush Community Development NFP</h4>
                  <p className="text-[11px] text-white/60 mt-1.5 leading-relaxed">Empowering communities through faith, education, and sustainable development. Partner with us.</p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#D4AF37] group-hover:gap-2 transition-all">
                    Support the mission
                    <span aria-hidden>&rarr;</span>
                  </div>
                </div>
              </a>
            </aside>
          </div>
        </div>

        {cw.length > 0 && (
          <div className="mb-12" data-testid="continue-watching-section">
            <CategoryRow title="Continue Watching" subtitle="Pick up where you left off" items={cw} />
          </div>
        )}

        <FeaturedAd />

        <AdSlot slot="homepage_banner" className="w-full h-32 md:h-40 mb-12" />

        <div className="mb-14"><ChannelGuide channels={channels} activeSlug={activeSlug} onSelect={handleSelect} /></div>

        {books.length > 0 && (
          <section className="mb-14" data-testid="book-series-section">
            <div className="flex items-end justify-between mb-4 px-1">
              <div>
                <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-1 flex items-center gap-2"><BookOpen className="w-3 h-3"/>Featured Book Series</p>
                <h3 className="font-heading text-3xl font-bold text-white">The VOT Living Book Series</h3>
                <p className="text-sm text-white/60 mt-2 max-w-2xl">A curated collection of devotional and theological works published by VOT Living. Watch companion teaching sessions on-air, and read the source volumes below.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {books.map(b => (
                <div key={b.id} className="content-card card-shine relative rounded-xl overflow-hidden border border-white/10 bg-[#121216]" data-testid={`book-${b.id}`}>
                  <div className="aspect-[2/3] overflow-hidden bg-black"><img src={b.cover_url} alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-3">
                    <p className="text-[10px] text-[#D4AF37] tracking-widest uppercase">{b.tags[0] || "Volume"}</p>
                    <h4 className="font-heading text-sm font-semibold text-white leading-tight mt-1 line-clamp-2">{b.title}</h4>
                    <p className="text-[11px] text-white/50 mt-1">{b.author}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="space-y-12">
          {categories.map((cat, i) => <CategoryRow key={cat} title={cat} subtitle={i === 0 ? "Featured Collection" : undefined} items={byCategory(cat)} />)}
        </div>

        <footer className="mt-24 border-t border-white/10 pt-8 pb-16 text-center">
          <p className="font-heading text-2xl text-white">VOT<span className="text-[#D4AF37]">Living</span>.tv</p>
          <p className="text-xs text-white/40 mt-2 tracking-widest uppercase">Broadcasting Light in a Digital Age</p>
        </footer>
      </div>
    </div>
  );
}

function ChannelTileButton({ channel, isActive, onSelect }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      data-testid={`channel-${channel.slug}`}
      className={`channel-card w-full flex items-center gap-3 p-3 rounded-xl border text-left transition ${isActive ? "bg-[#D4AF37]/12 border-[#D4AF37]/40" : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"}`}
    >
      <ChannelTilePreview
        streamUrl={channel.stream_url}
        streamType={channel.stream_type || "hls"}
        logoUrl={channel.logo_url}
        alt={channel.name}
        alwaysOn={hover}
        className="w-14 h-14 rounded-lg flex-shrink-0 border border-white/10"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-heading font-bold text-white truncate">{channel.name}</p>
          {isActive && <span className="text-[9px] font-bold tracking-widest bg-[#E11D48] px-1.5 py-0.5 rounded text-white">LIVE</span>}
        </div>
        <p className="text-[11px] text-white/60 truncate mt-0.5">{channel.now_playing?.title || channel.tagline}</p>
      </div>
    </button>
  );
}

